import { showRepPicker } from "@/analytics/core/scope-entities";
import { MultiCombobox } from "@/components/ui/multi-combobox";
import { DataTable } from "@/components/table";
import { getRawDataset, type RawDatasetId } from "@/raw/datasets/registry";
import { repOptionsForTeams, useRawDimensionOptions } from "@/raw/hooks/useRawDimensionOptions";
import { useRawScopeParams } from "@/raw/hooks/useRawScopeParams";
import { filterRowsByScope } from "@/raw/lib/filter-by-scope";
import { expandRawTeamSelection, teamOptionsFromGroups } from "@/raw/lib/team-groups";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

interface RawDatasetPageProps {
  datasetId: RawDatasetId;
}

export function RawDatasetPage({ datasetId }: RawDatasetPageProps) {
  const dataset = getRawDataset(datasetId);
  const repPicker = dataset.repPicker ?? "single-team";
  const { selection, setTeams, setReps } = useRawScopeParams(repPicker);
  const dimensions = useRawDimensionOptions();

  const { data, isLoading, error } = useQuery({
    queryKey: dataset.queryKey,
    queryFn: dataset.fetchRows,
  });

  const teamGroups = dimensions.data?.teamGroups ?? [];

  const teamOptions = useMemo(() => teamOptionsFromGroups(teamGroups), [teamGroups]);

  const expandedTeamIds = useMemo(
    () => expandRawTeamSelection(selection.teamIds, teamGroups),
    [selection.teamIds, teamGroups],
  );

  const repOptions = useMemo(
    () => repOptionsForTeams(dimensions.data?.reps ?? [], expandedTeamIds),
    [dimensions.data?.reps, expandedTeamIds],
  );

  const filteredRows = useMemo(() => {
    const rows = (data ?? []) as Record<string, unknown>[];
    return filterRowsByScope(
      rows,
      dataset.scopeFilter,
      { ...selection, teamIds: expandedTeamIds },
      repPicker,
    );
  }, [data, dataset.scopeFilter, expandedTeamIds, repPicker, selection]);

  const showFilters = dataset.scopeFilter !== "none";
  const showRepFilter =
    dataset.scopeFilter === "team-rep" && (repPicker === "always" || showRepPicker(selection));

  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-white p-5 shadow-sm sm:p-6">
      <header className="mb-6 space-y-3">
        <div className="space-y-1">
          <h1 className="text-lg font-semibold tracking-tight text-[var(--color-text)]">
            {dataset.title}
          </h1>
          <p className="text-sm text-[var(--color-text-muted)]">{dataset.description}</p>
        </div>

        {showFilters ? (
          <div className="flex flex-wrap items-center gap-2">
            <MultiCombobox
              allLabel="All teams"
              options={teamOptions}
              selectedIds={selection.teamIds}
              onApply={setTeams}
              searchPlaceholder="Search teams…"
            />
            {showRepFilter ? (
              <MultiCombobox
                allLabel="All reps"
                options={repOptions}
                selectedIds={selection.repIds}
                onApply={setReps}
                searchPlaceholder="Search reps…"
              />
            ) : null}
          </div>
        ) : null}
      </header>

      <DataTable
        embedded
        data={filteredRows}
        columns={dataset.columns}
        isLoading={isLoading}
        error={error instanceof Error ? error.message : error ? String(error) : null}
        searchColumnId={dataset.searchColumnId}
        searchPlaceholder={`Search ${dataset.title.toLowerCase()}…`}
        exportFilename={dataset.exportFilename}
      />
    </div>
  );
}
